import React, { useState } from 'react';
import {
  Image, StyleSheet, View, Text, TouchableOpacity,
} from 'react-native';
import { FileUploader } from 'react-drag-drop-files';
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';
import DownloadIcon16 from '../../16pxIcons/DownloadIcon16';
import Colors from '../../../constants/Colors';
import Style from '../../../constants/Style';
import ImageIcon16 from '../../16pxIcons/ImageIcon16';
import DeleteIcon16 from '../../16pxIcons/DeleteIcon16';
import AlertModal from '../../AlertModal';
import { getFileNameFromPath } from '../../../constants/Utils';
// import { MAXIMUM_IMAGE_SIZE } from '../../../constants/AlertMessages';

const maxSize: number = 20;

interface FloorPlanBoxProps {
  plan: string,
  planSize: number,
  planFilename: string,
  // eslint-disable-next-line no-unused-vars
  updatePlan: (newPlan: string) => void,
  // eslint-disable-next-line no-unused-vars
  updatePlanSize: (newSize: number) => void,
  // eslint-disable-next-line no-unused-vars
  updatePlanFilename: (newFilename: string) => void,
  onPress: () => void,
  Svg: string,
}

const FloorPlanBox = ({
  plan, planSize, planFilename, Svg, updatePlan, updatePlanSize, updatePlanFilename, onPress,
}: FloorPlanBoxProps) => {
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>('');

  const showAlert = (message: string) => {
    setAlertMessage(message);
    setAlertVisible(true);
  };

  // eslint-disable-next-line no-undef
  const reader = new FileReader();

  // warning if image over 2000
  // reader.onload = (e) => {
  //   const originalFile = e.target.result.toString();
  //   Image.getSize(originalFile, (w, h) => {
  //     if (h > 2000 || w > 2000) {
  //       showAlert(MAXIMUM_IMAGE_SIZE);
  //     } else {
  //       updatePlan(e.target.result.toString());
  //     }
  //   });
  // };

  // // if we wanted to scale down floorplans
  reader.onload = (e) => {
    const originalFile = e.target.result.toString();
    Image.getSize(originalFile, (w, h) => {
      if (h > 2000) {
        manipulateAsync(originalFile, [{ resize: { height: 2000 } }], {
          base64: true,
          compress: 1,
          format: SaveFormat.PNG,
        }).then((m) => {
          updatePlan(`data:image/png;base64,${m.base64}`);
        });
      } else if (w > 2000) {
        manipulateAsync(originalFile, [{ resize: { width: 2000 } }], {
          base64: true,
          compress: 1,
          format: SaveFormat.PNG,
        }).then((m) => {
          updatePlan(`data:image/png;base64,${m.base64}`);
        });
      } else {
        updatePlan(e.target.result.toString());
      }
    });
  };

  // not scaling down
  // reader.onload = (e) => updatePlan(e.target.result.toString());

  const deleteFloorPlan = () => {
    updatePlan(null);
    updatePlanSize(null);
    updatePlanFilename(null);
  };

  const handleChange = (file) => {
    onPress();
    updatePlanSize(Math.ceil(file.size / 100000) / 10);
    updatePlanFilename(file.name);
    reader.readAsDataURL(file);
  };

  return <View style={styles.wrapper}>
    <Text style={styles.title}>{'Upload floor plan'}</Text>
    <FileUploader
      types={['png', 'jpeg', 'jpg']}
      onTypeError={showAlert}
      onSizeError={showAlert}
      {...{ handleChange, maxSize }}
    >
      <View
        style={styles.container}
        testID='add-floor-plan-btn'
      >
        <View style={styles.icon}>
          <DownloadIcon16 />
        </View>
        <Text style={styles.dragAndDropTitle}>{'Drag and drop'}</Text>
        <View style={Style.row}>
          <Text style={styles.dragAndDropDescription}>{'or'} </Text>
          <Text style={[styles.dragAndDropDescription, styles.selectFileText]}>
            {'select .jpg or .png file'}
          </Text>
          <Text style={styles.dragAndDropDescription}>
            {' from computer'}
          </Text>
        </View>
        <Text style={styles.dragAndDropDescription}>{`File size limit of ${maxSize}MB`}</Text>
      </View>
    </FileUploader>
    {(plan || planFilename) && <View style={[Style.row, styles.currentPlanBox]}>
      <View style={styles.fileNameContainer}>
        <ImageIcon16 />
        <Text
          style={styles.fileName}
          numberOfLines={1}
          ellipsizeMode='middle'
        >
          {getFileNameFromPath(planFilename) ?? 'Unknown file'}
        </Text>
      </View>
      <View style={Style.row}>
        <Text>{`${planSize ?? '-'} MB`}</Text>
        {!Svg && <TouchableOpacity
          style={styles.deleteIcon}
          onPress={deleteFloorPlan}
        >
          <DeleteIcon16 color={Colors.light.hereworksBlue100} />
        </TouchableOpacity>}
      </View>
    </View>}
    <AlertModal
      modalVisible={alertVisible}
      setModalVisible={setAlertVisible}
      title='Error'
      message={alertMessage}
    />
  </View>;
};

const styles = StyleSheet.create({
  wrapper: {
    zIndex: -1,
  },
  title: {
    fontSize: 20,
    lineHeight: 30,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Roboto_500Medium',
    marginBottom: 12,
  },
  container: {
    paddingVertical: 24,
    borderWidth: 1,
    borderRadius: 5,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: Colors.light.hereworksWhite100,
    justifyContent: 'center',
    alignItems: 'center',
  },
  icon: {
    padding: 16,
    backgroundColor: Colors.light.hereworksBlue50,
    height: 64,
    aspectRatio: 1,
    borderRadius: 32,
    marginBottom: 12,
  },
  dragAndDropTitle: {
    fontSize: 18,
    lineHeight: 27,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_600SemiBold',
    textAlign: 'center',
    marginBottom: 8,
  },
  dragAndDropDescription: {
    color: Colors.light.hereworksBlack400,
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_500Medium',
    textAlign: 'center',
  },
  selectFileText: {
    color: Colors.light.hereworksBlue600,
  },
  currentPlanBox: {
    marginTop: 12,
    paddingVertical: 11,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderRadius: 5,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: Colors.light.hereworksWhite100,
    flex: 1,
  },
  fileNameContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  fileName: {
    fontSize: 12,
    lineHeight: 18,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginHorizontal: 8,
  },
  fileSize: {
    color: Colors.light.hereworksBlack400,
  },
  deleteIcon: {
    marginStart: 8,
  },
});

export default FloorPlanBox;
